/**
 * Time Frames API client.
 *
 * Time frames are reusable schedule definitions used by scheduling IVR
 * nodes (timer / day_of_week / switch_timer). See the backend's
 * `timeFrames` module and `docs/knowledge-base/features/time-frames.md`
 * for the full feature reference.
 */
import { apiFetch } from './client';

export type TimeFrameMode = 'always' | 'days_of_week' | 'specific_dates';

export type DayKey = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

export interface DaysOfWeekSlot {
  day: DayKey;
  from: string;  // 'HH:MM'
  to: string;    // 'HH:MM'
}

export interface SpecificDateRange {
  from: string;  // 'YYYY-MM-DDTHH:MM' or 'YYYY-MM-DD HH:MM'
  to: string;
}

/**
 * Rules jsonb shape — mode-specific. We type it as a loose record
 * because TS can't model "this exact shape iff mode is X" cleanly
 * without conditional types making consumer code ugly. The server
 * validates the shape against the mode on every write.
 *
 *   mode='always'         → {}
 *   mode='days_of_week'   → { slots: DaysOfWeekSlot[] }
 *   mode='specific_dates' → { ranges: SpecificDateRange[] }
 */
export interface TimeFrameRules {
  slots?: DaysOfWeekSlot[];
  ranges?: SpecificDateRange[];
}

export interface TimeFrameRow {
  id: string;
  reseller_id: string;
  customer_id: string;
  /**
   * Customer-scoped sequential id. Surfaced as `TF-${n}` in URLs and
   * the portal UI. Lookup endpoints accept either UUID or TF-N.
   */
  ref_number: number;
  name: string;
  mode: TimeFrameMode;
  rules: TimeFrameRules;
  timezone: string;
  active: boolean;
  /** User who created the frame, or null for pre-0077 rows. */
  created_by: string | null;
  /**
   * Display name of the creator (display_name → email fallback).
   * Populated by list/get via JOIN. Null when created_by is null.
   */
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Shape of the 409 body returned when DELETE /api/time-frames/:id is
 * rejected because IVR nodes still reference the frame. Use this to
 * render the "Cannot delete — used by these nodes" UI.
 */
export interface TimeFrameInUseBody {
  error: 'time_frame_in_use';
  message: string;
  blocking_nodes: Array<{
    id: string;
    ref_number: string;
    name: string;
    node_type: string;
  }>;
}

/** Format a TF ref_number for display + URLs.  formatTimeFrameRef(42) → "TF-42". */
export function formatTimeFrameRef(n: number): string {
  return `TF-${n}`;
}

export interface CreateTimeFrameBody {
  name: string;
  mode: TimeFrameMode;
  rules: TimeFrameRules;
  timezone?: string;
  active?: boolean;
  customer_id?: string;
  reseller_id?: string;
}

export interface PatchTimeFrameBody {
  /** Name is editable now (Sam 2026-05-20). TF-NNNN ref stays stable. */
  name?: string;
  mode?: TimeFrameMode;
  rules?: TimeFrameRules;
  timezone?: string;
  active?: boolean;
}

export interface EvaluateResult {
  id: string;
  ref_number: number;
  mode: TimeFrameMode;
  timezone: string;
  at: string;
  result: 'in_frame' | 'not_in_frame';
}

export const timeFrames = {
  list: (p: { customer_id?: string; active?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (p.customer_id) q.set('customer_id', p.customer_id);
    if (p.active !== undefined) q.set('active', String(p.active));
    const qs = q.toString();
    return apiFetch<{ rows: TimeFrameRow[] }>(
      `/api/time-frames${qs ? `?${qs}` : ''}`,
    );
  },

  get: (idOrRef: string) =>
    apiFetch<TimeFrameRow>(`/api/time-frames/${idOrRef}`),

  create: (body: CreateTimeFrameBody) =>
    apiFetch<TimeFrameRow>('/api/time-frames', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: (idOrRef: string, patch: PatchTimeFrameBody) =>
    apiFetch<TimeFrameRow>(`/api/time-frames/${idOrRef}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  remove: (idOrRef: string) =>
    apiFetch<void>(`/api/time-frames/${idOrRef}`, { method: 'DELETE' }),

  /**
   * Evaluate the frame at a given moment (defaults to now). Used by the
   * detail page's "Currently in frame / not in frame" badge.
   */
  evaluate: (idOrRef: string, at?: string) => {
    const q = at ? `?at=${encodeURIComponent(at)}` : '';
    return apiFetch<EvaluateResult>(`/api/time-frames/${idOrRef}/evaluate${q}`);
  },
};
